import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { LayoutGrid } from 'lucide-react';
import { listarProjetos } from '@/features/projetos/projetos.api';
import { SkeletonText } from '@/components/Skeleton';
import { cn } from '@/lib/cn';

// Mesmas cores que ProjetosPage.STATUS_BAR — copiadas intencionalmente
// para que a feature seja autossuficiente (tokens não compartilhados).
const STATUS_BAR: Record<string, string> = {
  DRAFT: 'bg-muted-foreground/30',
  PLANNING: 'bg-warning',
  ACTIVE: 'bg-primary',
  ON_HOLD: 'bg-warning/60',
  COMPLETED: 'bg-green-500',
  CANCELLED: 'bg-destructive/50',
};

function fmtData(ep: number | null): string {
  if (!ep) return '—';
  return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

export function PortfolioPage() {
  const { data, isPending } = useQuery({
    queryKey: ['projetos', 'portfolio'],
    queryFn: () => listarProjetos({ status: 'ACTIVE' }),
  });

  const projetos = data?.items ?? [];

  // Projetos que têm datas definem o intervalo do eixo do tempo.
  const comDatas = projetos.filter((p) => p.startAt != null && p.finishAt != null);
  const minStart = comDatas.length ? Math.min(...comDatas.map((p) => p.startAt!)) : 0;
  const maxFinish = comDatas.length ? Math.max(...comDatas.map((p) => p.finishAt!)) : 0;
  const span = maxFinish - minStart || 1;

  const now = Math.floor(Date.now() / 60_000);
  const todayPct = comDatas.length
    ? Math.max(0, Math.min(100, ((now - minStart) / span) * 100))
    : null;

  if (isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonText className="h-6 w-48" />
        <SkeletonText className="h-4 w-72" />
        <SkeletonText className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <LayoutGrid className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h1 className="text-lg font-semibold">Portfólio</h1>
          <p className="text-sm text-muted-foreground">
            Linha do tempo de projetos ativos
          </p>
        </div>
      </div>

      {projetos.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Nenhum projeto ativo no momento.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <div className="min-w-[700px]">
            {/* Linha de cabeçalho da tabela */}
            <div className="flex border-b border-border bg-muted/30">
              <div className="w-60 shrink-0 px-4 py-2 text-xs font-medium text-muted-foreground">
                Projeto
              </div>
              <div className="flex-1 px-3 py-2">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground/70">
                  {comDatas.length > 0 ? (
                    <>
                      <span>{fmtData(minStart)}</span>
                      <span>Hoje</span>
                      <span>{fmtData(maxFinish)}</span>
                    </>
                  ) : (
                    <span>Linha do tempo</span>
                  )}
                </div>
              </div>
            </div>

            {/* Linhas de projeto */}
            {projetos.map((p) => {
              const temDatas = p.startAt != null && p.finishAt != null;
              const leftPct = temDatas
                ? ((p.startAt! - minStart) / span) * 100
                : 0;
              const widthPct = temDatas
                ? ((p.finishAt! - p.startAt!) / span) * 100
                : 0;
              const barCls = STATUS_BAR[p.status] ?? 'bg-muted-foreground/30';

              return (
                <div
                  key={p.id}
                  className="flex border-b border-border last:border-0 hover:bg-muted/10 transition-colors"
                >
                  {/* Coluna do nome */}
                  <div className="w-60 shrink-0 px-4 py-3">
                    <Link
                      to="/projetos/$projetoId"
                      params={{ projetoId: p.id }}
                      className="block text-sm font-medium leading-snug hover:text-primary line-clamp-1"
                    >
                      {p.name}
                    </Link>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {p.number}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {p.progressPercent}%
                      </span>
                    </div>
                    {/* Mini barra de progresso */}
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${p.progressPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Área da barra de tempo */}
                  <div className="relative flex-1 py-4 pr-3">
                    {temDatas ? (
                      <div
                        className={cn('absolute top-1/2 -translate-y-1/2 rounded', barCls)}
                        style={{
                          left: `${leftPct}%`,
                          width: `${Math.max(widthPct, 0.5)}%`,
                          height: '20px',
                        }}
                        title={`${p.name} · ${fmtData(p.startAt)} – ${fmtData(p.finishAt)}`}
                      />
                    ) : (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/40">
                        Sem datas
                      </span>
                    )}

                    {/* Linha de hoje */}
                    {todayPct !== null && (
                      <div
                        className="pointer-events-none absolute bottom-0 top-0 border-l-2 border-dashed border-red-500"
                        style={{ left: `${todayPct}%` }}
                        aria-hidden
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {comDatas.length < projetos.length && (
        <p className="text-xs text-muted-foreground">
          {projetos.length - comDatas.length} projeto(s) sem datas de início/término não
          aparecem na linha do tempo.
        </p>
      )}
    </div>
  );
}
